import {
  canonicalToCanvas,
  createPointArray,
  applyRegionScaleAsymY,
  decimateTriangles,
  computeEyeGaze,
  selectNearestIndices,
  translateIndicesY,
  translateIndicesWeighted,
  getEyeCenter,
  avgY
} from './faceMath'
import {
  FACE_MESH_CONTOUR_PATHS,
  EYE_LAYER_POLYGONS,
  IRIS_INDICES
} from '../avatarInspector/featureIndices'
import { warpTriangles } from '../meshWarp'
import {
  clipToEyeSockets,
  fillEyeSockets,
  drawPupilCircles,
  computeIrisStats,
  drawEyelidOccluder,
  sampleLidColor
} from './eyes'
import { drawRigAlignedLayer, drawRigAlignedLayerWithYaw } from './utils'
import { clamp, LANDMARKS, CHEEK_FEATURE_INDICES, meshQualityToStride } from './helpers'
import { resolveEyeBlinkState } from './eyeBlinkModel'
import { FACEMESH_TRIANGULATION } from '../faceMeshTriangulation'
import {
  expandFaceOvalPointsByIndices,
  computeBrowMinY,
  captureForeheadEdgeBackup,
  restoreForeheadEdgeBackup
} from '../faceMaskUtils'

const LEFT_BROW_IDX = (FACE_MESH_CONTOUR_PATHS?.leftBrow || []).filter((v) => Number.isInteger(v))
const RIGHT_BROW_IDX = (FACE_MESH_CONTOUR_PATHS?.rightBrow || []).filter((v) => Number.isInteger(v))
const BROW_IDX = [...LEFT_BROW_IDX, ...RIGHT_BROW_IDX]
const LEFT_EYE_POLY = (EYE_LAYER_POLYGONS?.left || []).filter((v) => Number.isInteger(v))
const RIGHT_EYE_POLY = (EYE_LAYER_POLYGONS?.right || []).filter((v) => Number.isInteger(v))

export function createMeshRenderer(options = {}) {
  const { canvasAdapter, resourceManager, getRuntimeState } = options || {}
  let adapter =
    canvasAdapter ||
    (resourceManager && resourceManager._internal && resourceManager._internal.adapter)
  if (!adapter) {
    // lazy-require adapter to avoid bundler issues
    try {
      const { createCanvasAdapter } = require('./canvasAdapter')
      adapter = createCanvasAdapter()
    } catch (e) {
      // fallback to OffscreenCanvas when available
      adapter = {
        createCanvas: (w, h) =>
          typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(w, h) : null,
        getContext: (canvas, type, opts) => (canvas ? canvas.getContext(type, opts) : null)
      }
    }
  }
  // helper to create canvases and register them with resourceManager for central disposal
  const createCanvasReg = (w, h) => {
    const c = adapter.createCanvas(w, h)
    try {
      if (c && resourceManager && typeof resourceManager.registerCanvas === 'function') {
        resourceManager.registerCanvas(c)
        if (typeof resourceManager.registerDisposable === 'function') {
          resourceManager.registerDisposable(() => {
            try {
              c.width = 0
              c.height = 0
            } catch (e) {}
          })
        }
      }
    } catch (e) {}
    return c
  }
  let warpDstPoints = null
  let eyeWarpDstPoints = null
  let eyeSocketDstPoints = null
  let warpDepthBuffer = null
  let faceTriangleWarpCache = { source: null, stride: 1, triangles: null }
  let gSmoothLX = 0
  let gSmoothLY = 0
  let gSmoothRX = 0
  let gSmoothRY = 0
  let irisOverrideKey = ''
  let irisOverrideValue = null
  let eyeCanonical = null
  const blinkCache = { left: 0, right: 0 }
  let cachedPaletteLayers = null
  let cachedPalette = null
  let baselineCanvasPoints = null
  let hairMeshDstPoints = null
  let hairMeshPointCount = 0
  let faceRigidDstPoints = null
  let faceRigidPointCount = 0
  const browBackup = {
    idx: BROW_IDX,
    x: new Float32Array(BROW_IDX.length),
    y: new Float32Array(BROW_IDX.length),
    z: new Float32Array(BROW_IDX.length),
    valid: new Uint8Array(BROW_IDX.length)
  }
  let warnedMissing = false

  const rgba = (r, g, b, a = 0.96) =>
    `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${a})`

  const adaptSmoothing = (base, delta, fast = 0.08) => {
    const b = clamp(Number(base) || 0, 0, 0.95)
    const t = clamp(Math.abs(delta) / 0.35, 0, 1)
    return b * (1 - t) + fast * t
  }

  const smoothChannelAR = (current, target, baseSmooth, attackMul = 0.95, releaseMul = 0.38) => {
    const cur = clamp(Number(current) || 0, 0, 1)
    const tgt = clamp(Number(target) || 0, 0, 1)
    const base = clamp(Number(baseSmooth) || 0.5, 0, 0.98)
    const response = 1 - base
    const attack = clamp(response * attackMul + 0.16, 0.08, 0.9)
    const release = clamp(response * releaseMul + 0.04, 0.03, 0.42)
    const k = tgt >= cur ? attack : release
    return cur + (tgt - cur) * k
  }

  const getPupilPalette = (layers) => {
    if (!layers) {
      cachedPaletteLayers = null
      cachedPalette = null
      return null
    }
    if (layers === cachedPaletteLayers) return cachedPalette
    cachedPaletteLayers = layers
    cachedPalette = layers?.pupilPalette || null
    return cachedPalette
  }

  const getIrisOverride = (settings) => {
    const raw = typeof settings?.irisColor === 'string' ? settings.irisColor.trim() : ''
    if (!raw) {
      irisOverrideKey = ''
      irisOverrideValue = null
      return null
    }
    if (raw === irisOverrideKey) return irisOverrideValue
    const hex = raw.replace('#', '')
    const h = hex.length === 3 ? `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}` : hex
    const r = parseInt(h.slice(0, 2), 16)
    const g = parseInt(h.slice(2, 4), 16)
    const b = parseInt(h.slice(4, 6), 16)
    if (!Number.isFinite(r) || !Number.isFinite(g) || !Number.isFinite(b)) {
      irisOverrideKey = raw
      irisOverrideValue = null
      return null
    }
    irisOverrideKey = raw
    irisOverrideValue = { left: { r, g, b }, right: { r, g, b } }
    return irisOverrideValue
  }

  // drawRigAlignedLayer provided by shared utils

  const drawFaceMeshOverlay = (ctx2d, pts, mode = 'tesselation') => {
    if (!ctx2d || !pts?.length) return
    ctx2d.save()
    ctx2d.lineWidth = 0.6
    ctx2d.strokeStyle = 'rgba(0, 220, 255, 0.85)'
    if (mode === 'tesselation') {
      for (let i = 0; i < FACEMESH_TRIANGULATION.length; i += 3) {
        const a = pts[FACEMESH_TRIANGULATION[i]]
        const b = pts[FACEMESH_TRIANGULATION[i + 1]]
        const c = pts[FACEMESH_TRIANGULATION[i + 2]]
        if (!a || !b || !c) continue
        ctx2d.beginPath()
        ctx2d.moveTo(a.x, a.y)
        ctx2d.lineTo(b.x, b.y)
        ctx2d.lineTo(c.x, c.y)
        ctx2d.closePath()
        ctx2d.stroke()
      }
    } else if (mode === 'contours') {
      const drawPath = (arr) => {
        if (!arr?.length) return
        const f = pts[arr[0]]
        if (!f) return
        ctx2d.beginPath()
        ctx2d.moveTo(f.x, f.y)
        for (let i = 1; i < arr.length; i += 1) {
          const p = pts[arr[i]]
          if (!p) continue
          ctx2d.lineTo(p.x, p.y)
        }
        ctx2d.stroke()
      }
      drawPath(FACE_MESH_CONTOUR_PATHS.faceOval)
      drawPath(FACE_MESH_CONTOUR_PATHS.leftEye)
      drawPath(FACE_MESH_CONTOUR_PATHS.rightEye)
      drawPath(FACE_MESH_CONTOUR_PATHS.leftBrow)
      drawPath(FACE_MESH_CONTOUR_PATHS.rightBrow)
      drawPath(FACE_MESH_CONTOUR_PATHS.lipsOuter)
      drawPath(FACE_MESH_CONTOUR_PATHS.lipsInner)
      drawPath(FACE_MESH_CONTOUR_PATHS.noseBridge)
    }
    ctx2d.restore()
  }

  const reset = () => {
    warpDstPoints = null
    eyeWarpDstPoints = null
    eyeSocketDstPoints = null
    warpDepthBuffer = null
    faceTriangleWarpCache = { source: null, stride: 1, triangles: null }
    gSmoothLX = 0
    gSmoothLY = 0
    gSmoothRX = 0
    gSmoothRY = 0
    eyeCanonical = null
    cachedPaletteLayers = null
    cachedPalette = null
    // lidColor caching moved to shared eyes helper
    warnedMissing = false
    hairMeshDstPoints = null
    hairMeshPointCount = 0
  }

  const processMesh = ({
    ctx,
    canvas,
    settings,
    avatarImage,
    avatarRig,
    avatarLayers,
    geometryManager,
    warpCanonical,
    liveCanonical,
    baselineCanonical,
    liveRigMetrics,
    meshQuality,
    blinkLeft,
    blinkRight,
    blinkPhaseL,
    blinkPhaseR,
    browSmoothL,
    browSmoothR,
    cheekSmoothL,
    cheekSmoothR,
    blendshapeSignals,
    showAvatarEyes,
    showAvatarFace,
    showAvatarHair,
    prefer2dHair = false,
    avatarKey,
    trace
  } = {}) => {
    const runtime = typeof getRuntimeState === 'function' ? getRuntimeState() : null

    let renderedByGeometry = false

    if (!ctx || !canvas || !settings)
      return { browSmoothL, browSmoothR, cheekSmoothL, cheekSmoothR, renderedByGeometry }
    if (!avatarImage?.img || !avatarRig?.pointsPx || !warpCanonical) {
      if (!warnedMissing) {
        warnedMissing = true
        console.warn('[AvatarEngine] Mesh renderer missing required inputs (avatarImage/rig/mesh).')
      }
      return { browSmoothL, browSmoothR, cheekSmoothL, cheekSmoothR, renderedByGeometry }
    }

    const resolvedCount = warpCanonical.length || 0

    const rigidMetrics =
      avatarRig?.faceMetrics && liveRigMetrics
        ? {
            ...liveRigMetrics,
            faceW:
              liveRigMetrics.faceH * (avatarRig.faceMetrics.faceW / avatarRig.faceMetrics.faceH)
          }
        : liveRigMetrics
    const eyeBlink = resolveEyeBlinkState({
      blinkLeft,
      blinkRight,
      blinkPhaseL,
      blinkPhaseR,
      settings
    })
    const eyeRenderBlinkLeft = eyeBlink.eyeRenderBlinkLeft
    const eyeRenderBlinkRight = eyeBlink.eyeRenderBlinkRight
    const eyelidBlinkLeft = eyeBlink.eyelidBlinkLeft
    const eyelidBlinkRight = eyeBlink.eyelidBlinkRight
    const lidStart = eyeBlink.lidStart
    const lidFull = eyeBlink.lidFull

    if (showAvatarEyes) {
      const rtEyeSocket = runtime?.eyeSocketDstPoints || runtime?.eyeSocketPoints || null
      eyeSocketDstPoints = rtEyeSocket
        ? rtEyeSocket
        : canonicalToCanvas(warpCanonical, rigidMetrics, eyeSocketDstPoints)
    }

    const leftBrowIdx = LEFT_BROW_IDX
    const rightBrowIdx = RIGHT_BROW_IDX
    const browIdx = BROW_IDX
    for (let i = 0; i < browIdx.length; i += 1) {
      const bi = browIdx[i]
      const p = warpCanonical[bi]
      if (p) {
        browBackup.x[i] = p.x
        browBackup.y[i] = p.y
        browBackup.z[i] = p.z || 0
        browBackup.valid[i] = 1
      } else {
        browBackup.valid[i] = 0
      }
    }

    // Eye closure is handled by eyelid occluders on the eye socket perimeter.
    // Avoid additional blink-time eye-region mesh scaling to prevent deformation artifacts.

    if (browIdx.length) {
      for (let i = 0; i < browIdx.length; i += 1) {
        if (!browBackup.valid[i]) continue
        const bi = browIdx[i]
        const p = warpCanonical[bi]
        if (!p) continue
        p.x = browBackup.x[i]
        p.y = browBackup.y[i]
        p.z = browBackup.z[i]
      }
    }

    const foreheadEdgeBackup = captureForeheadEdgeBackup(warpCanonical, FACE_MESH_CONTOUR_PATHS)

    try {
      const lb = LEFT_BROW_IDX
      const rb = RIGHT_BROW_IDX
      const lEye = getEyeCenter(
        liveCanonical,
        LANDMARKS.leftEye,
        LANDMARKS.leftEyeInner,
        LANDMARKS.leftEyeUpper,
        LANDMARKS.leftEyeLower
      )
      const rEye = getEyeCenter(
        liveCanonical,
        LANDMARKS.rightEye,
        LANDMARKS.rightEyeInner,
        LANDMARKS.rightEyeUpper,
        LANDMARKS.rightEyeLower
      )
      const lEyeBase = baselineCanonical
        ? getEyeCenter(
            baselineCanonical,
            LANDMARKS.leftEye,
            LANDMARKS.leftEyeInner,
            LANDMARKS.leftEyeUpper,
            LANDMARKS.leftEyeLower
          )
        : null
      const rEyeBase = baselineCanonical
        ? getEyeCenter(
            baselineCanonical,
            LANDMARKS.rightEye,
            LANDMARKS.rightEyeInner,
            LANDMARKS.rightEyeUpper,
            LANDMARKS.rightEyeLower
          )
        : null
      const lBrowY = avgY(liveCanonical, lb)
      const rBrowY = avgY(liveCanonical, rb)
      const lBrowBaseY = baselineCanonical ? avgY(baselineCanonical, lb) : null
      const rBrowBaseY = baselineCanonical ? avgY(baselineCanonical, rb) : null
      if (lEye && rEye && lBrowY != null && rBrowY != null) {
        const lRef = lEyeBase && lBrowBaseY != null ? lBrowBaseY - lEyeBase.y : 0.12
        const rRef = rEyeBase && rBrowBaseY != null ? rBrowBaseY - rEyeBase.y : 0.12
        const lNow = lBrowY - lEye.y
        const rNow = rBrowY - rEye.y
        const lDelta = clamp((lRef - lNow) / Math.max(0.04, Math.abs(lRef)), -1, 1)
        const rDelta = clamp((rRef - rNow) / Math.max(0.04, Math.abs(rRef)), -1, 1)
        const sK = adaptSmoothing(
          settings.browSmoothing,
          Math.max(Math.abs(lDelta), Math.abs(rDelta))
        )
        browSmoothL = browSmoothL * sK + (1 - sK) * lDelta
        browSmoothR = browSmoothR * sK + (1 - sK) * rDelta
        const amp = clamp(Number(settings.browStrength) || 1, 0, 2)
        const baseScale = 0.06
        const dyL = -browSmoothL * baseScale * amp
        const dyR = -browSmoothR * baseScale * amp
        if (Math.abs(dyL) > 1e-4) translateIndicesY(warpCanonical, lb, dyL)
        if (Math.abs(dyR) > 1e-4) translateIndicesY(warpCanonical, rb, dyR)
      }
    } catch {}

    try {
      const lb = LEFT_BROW_IDX
      const rb = RIGHT_BROW_IDX
      if (lb.length && rb.length) {
        const midX =
          ((liveCanonical[LANDMARKS.leftEye]?.x || 0) +
            (liveCanonical[LANDMARKS.rightEye]?.x || 0)) *
          0.5
        const pickInner = (arr, pts, n = 4) =>
          arr
            .map((idx) => ({ idx, p: pts[idx] }))
            .filter((o) => !!o.p)
            .sort((a, b) => Math.abs(a.p.x - midX) - Math.abs(b.p.x - midX))
            .slice(0, n)
            .map((o) => o.idx)
        const lInner = pickInner(lb, liveCanonical)
        const rInner = pickInner(rb, liveCanonical)
        const avgInnerY = (arr, pts) => {
          let s = 0,
            c = 0
          for (let i = 0; i < arr.length; i += 1) {
            const p = pts[arr[i]]
            if (!p) continue
            s += p.y
            c += 1
          }
          return c ? s / c : null
        }
        const lInY = avgInnerY(lInner, liveCanonical)
        const rInY = avgInnerY(rInner, liveCanonical)
        const lInBaseY = baselineCanonical ? avgInnerY(lInner, baselineCanonical) : null
        const rInBaseY = baselineCanonical ? avgInnerY(rInner, baselineCanonical) : null
        if (lInY != null && rInY != null) {
          const fL = lInBaseY != null ? clamp((lInY - lInBaseY) / 0.05, 0, 1) : 0
          const fR = rInBaseY != null ? clamp((rInY - rInBaseY) / 0.05, 0, 1) : 0
          const amp = clamp(Number(settings.browFrownStrength) || 1, 0, 2)
          const pinchAmp = clamp(Number(settings.browPinchStrength) || 1, 0, 2)
          const downBase = 0.055 * amp
          const inBase = 0.04 * amp
          const applyFrown = (indices, factor) => {
            for (let i = 0; i < indices.length; i += 1) {
              const idx = indices[i]
              const p = warpCanonical[idx]
              if (!p) continue
              const dxDir = Math.sign(midX - p.x)
              const dmx = Math.abs(p.x - midX)
              const w = Math.exp(-(dmx * dmx) / (2 * 0.12 * 0.12))
              p.y += downBase * factor * w
              p.x += inBase * factor * dxDir * w
            }
          }
          applyFrown(lb, fL)
          applyFrown(rb, fR)
          const pinchF = Math.pow(Math.max(fL, fR), 1.05)
          const pinchOnly = (indices) => {
            for (let i = 0; i < indices.length; i += 1) {
              const idx = indices[i]
              const p = warpCanonical[idx]
              if (!p) continue
              const dxDir = Math.sign(midX - p.x)
              const dmx = Math.abs(p.x - midX)
              const wInner = Math.exp(-(dmx * dmx) / (2 * 0.08 * 0.08))
              p.x += 0.055 * pinchAmp * pinchF * dxDir * wInner
            }
          }
          pinchOnly(lInner)
          pinchOnly(rInner)

          const eyeAmp = clamp(Number(settings.eyeFrownStrength) || 0, 0, 0)
          const lInnerCorner = liveCanonical[LANDMARKS.leftEyeInner] || null
          const rInnerCorner = liveCanonical[LANDMARKS.rightEyeInner] || null
          if (lInnerCorner) {
            const nearL = selectNearestIndices(liveCanonical, LEFT_EYE_POLY, lInnerCorner, 8)
            const dy = 0.055 * amp * Math.max(fL, 0) * eyeAmp
            const dx = 0.035 * amp * Math.max(fL, 0) * eyeAmp
            translateIndicesWeighted(
              warpCanonical,
              nearL,
              Math.sign(midX - lInnerCorner.x) * dx,
              dy,
              lInnerCorner,
              0.14
            )
          }
          if (rInnerCorner) {
            const nearR = selectNearestIndices(liveCanonical, RIGHT_EYE_POLY, rInnerCorner, 8)
            const dy = 0.055 * amp * Math.max(fR, 0) * eyeAmp
            const dx = 0.035 * amp * Math.max(fR, 0) * eyeAmp
            translateIndicesWeighted(
              warpCanonical,
              nearR,
              Math.sign(midX - rInnerCorner.x) * dx,
              dy,
              rInnerCorner,
              0.14
            )
          }

          // Couple brow-down with a gentle upper-lid descent so brows don't pass under a rigid lid.
          // This scales the region above each eye center, affecting mostly the upper lid arc.
          const lidCouple = clamp(Number(settings.eyeLidBrowCouple) || 0.6, 0, 1)
          if (avatarRig?.leftEyeCenter) {
            const su = 1 - 0.12 * lidCouple * Math.max(fL, 0)
            const sl = 1.0
            applyRegionScaleAsymY(
              warpCanonical,
              avatarRig.leftEyeCenter,
              (avatarRig.leftEyeRadiusX || 0) * 1.2,
              (avatarRig.leftEyeRadiusY || 0) * 1.0,
              su,
              sl
            )
          }
          if (avatarRig?.rightEyeCenter) {
            const su = 1 - 0.12 * lidCouple * Math.max(fR, 0)
            const sl = 1.0
            applyRegionScaleAsymY(
              warpCanonical,
              avatarRig.rightEyeCenter,
              (avatarRig.rightEyeRadiusX || 0) * 1.2,
              (avatarRig.rightEyeRadiusY || 0) * 1.0,
              su,
              sl
            )
          }
        }
      }
    } catch {}

    restoreForeheadEdgeBackup(warpCanonical, foreheadEdgeBackup)

    try {
      const mouthL = liveCanonical[LANDMARKS.mouthLeft]
      const mouthR = liveCanonical[LANDMARKS.mouthRight]
      const mouthLBase = baselineCanonical?.[LANDMARKS.mouthLeft] || null
      const mouthRBase = baselineCanonical?.[LANDMARKS.mouthRight] || null
      if (mouthL && mouthR) {
        const bL = mouthLBase || mouthL
        const bR = mouthRBase || mouthR
        const liftL = clamp((bL.y - mouthL.y) / 0.06, -1, 1)
        const liftR = clamp((bR.y - mouthR.y) / 0.06, -1, 1)
        const sK = adaptSmoothing(
          settings.cheekSmoothing,
          Math.max(Math.abs(liftL), Math.abs(liftR))
        )
        const map = blendshapeSignals || {}
        const bsConfidence = clamp(Number(map.confidence) || 0, 0, 1)
        const bsSmileL = clamp(Number(map.mouthSmileLeft) || 0, 0, 1)
        const bsSmileR = clamp(Number(map.mouthSmileRight) || 0, 0, 1)
        const bsSquintL = clamp(Number(map.cheekSquintLeft) || 0, 0, 1)
        const bsSquintR = clamp(Number(map.cheekSquintRight) || 0, 0, 1)
        const bsPuff = clamp(Number(map.cheekPuff) || 0, 0, 1)
        const landmarkLiftL = Math.max(0, liftL)
        const landmarkLiftR = Math.max(0, liftR)
        const bsLiftL = clamp(bsSmileL * 0.75 + bsSquintL * 0.55 + bsPuff * 0.18, 0, 1)
        const bsLiftR = clamp(bsSmileR * 0.75 + bsSquintR * 0.55 + bsPuff * 0.18, 0, 1)
        const blendWeight = bsConfidence > 0.05 ? 0.45 + bsConfidence * 0.45 : 0
        const targetCheekL = clamp(landmarkLiftL * (1 - blendWeight) + bsLiftL * blendWeight, 0, 1)
        const targetCheekR = clamp(landmarkLiftR * (1 - blendWeight) + bsLiftR * blendWeight, 0, 1)
        cheekSmoothL = smoothChannelAR(cheekSmoothL, targetCheekL, sK)
        cheekSmoothR = smoothChannelAR(cheekSmoothR, targetCheekR, sK)
        const amp = clamp(Number(settings.cheekStrength) || 1, 0, 2)
        const up = 0.035 * amp
        const out = 0.04 * amp
        const dyL = -up * cheekSmoothL
        const dxL = -out * cheekSmoothL
        const dyR = -up * cheekSmoothR
        const dxR = out * cheekSmoothR
        translateIndicesWeighted(warpCanonical, CHEEK_FEATURE_INDICES, dxL, dyL, mouthL, 0.22)
        translateIndicesWeighted(warpCanonical, CHEEK_FEATURE_INDICES, dxR, dyR, mouthR, 0.22)
      }
    } catch {}

    if (showAvatarEyes) {
      eyeSocketDstPoints = canonicalToCanvas(warpCanonical, rigidMetrics, eyeSocketDstPoints)
    }

    let eyeOverlayPoints = null
    if (showAvatarEyes && liveCanonical) {
      let gaze = computeEyeGaze(liveCanonical, avatarRig)
      const dz = clamp(Number(settings.gazeDeadzone) || 0.04, 0, 0.3)
      const sK = clamp(Number(settings.gazeSmoothing) || 0.6, 0, 0.98)
      const decouple = (g) => {
        const yaw =
          (liveCanonical[LANDMARKS.leftEye]?.z || 0) - (liveCanonical[LANDMARKS.rightEye]?.z || 0)
        const pitch =
          ((liveCanonical[LANDMARKS.leftEyeUpper]?.y || 0) +
            (liveCanonical[LANDMARKS.rightEyeUpper]?.y || 0)) *
            0.5 -
          ((baselineCanonical?.[LANDMARKS.leftEyeUpper]?.y || 0) +
            (baselineCanonical?.[LANDMARKS.rightEyeUpper]?.y || 0)) *
            0.5
        return {
          x: g.x - yaw * 0.9,
          y: g.y - pitch * 0.6
        }
      }
      const softDeadzone = (v) => {
        const a = Math.abs(v)
        if (a <= dz) return 0
        const t = (a - dz) / Math.max(1e-4, 1 - dz)
        return Math.sign(v) * t
      }
      const cal = (g, side = 'left') => {
        const ox =
          side === 'left' ? Number(settings.gazeOffsetLX) || 0 : Number(settings.gazeOffsetRX) || 0
        const oy =
          side === 'left' ? Number(settings.gazeOffsetLY) || 0 : Number(settings.gazeOffsetRY) || 0
        const gx =
          side === 'left' ? Number(settings.gazeGainLX) || 1 : Number(settings.gazeGainRX) || 1
        const gy =
          side === 'left' ? Number(settings.gazeGainLY) || 1 : Number(settings.gazeGainRY) || 1
        const d = decouple({ x: g.x - ox, y: g.y - oy })
        return { x: softDeadzone(d.x) * gx, y: softDeadzone(d.y) * gy }
      }
      const cL = cal(gaze.left, 'left')
      const cR = cal(gaze.right, 'right')
      gSmoothLX = sK * gSmoothLX + (1 - sK) * cL.x
      gSmoothLY = sK * gSmoothLY + (1 - sK) * cL.y
      gSmoothRX = sK * gSmoothRX + (1 - sK) * cR.x
      gSmoothRY = sK * gSmoothRY + (1 - sK) * cR.y
      gaze = { left: { x: gSmoothLX, y: gSmoothLY }, right: { x: gSmoothRX, y: gSmoothRY } }

      const fs = Math.max(0, Math.min(1, Number(settings.irisFreezeStart) || 0.6))
      const ff = Math.max(fs + 0.02, Math.min(1, Number(settings.irisFreezeFull) || 0.8))
      const freezeFactorL =
        eyeRenderBlinkLeft <= fs
          ? 1
          : eyeRenderBlinkLeft >= ff
            ? 0
            : (ff - eyeRenderBlinkLeft) / (ff - fs)
      const freezeFactorR =
        eyeRenderBlinkRight <= fs
          ? 1
          : eyeRenderBlinkRight >= ff
            ? 0
            : (ff - eyeRenderBlinkRight) / (ff - fs)
      if (!eyeCanonical || eyeCanonical.length !== resolvedCount) {
        eyeCanonical = createPointArray(resolvedCount)
      }
      for (let i = 0; i < resolvedCount; i += 1) {
        const src = warpCanonical[i]
        const dst = eyeCanonical[i]
        if (!src || !dst) continue
        dst.x = src.x
        dst.y = src.y
        dst.z = src.z || 0
      }

      if (avatarRig.leftEyeCenter) {
        const leftShiftX = gaze.left.x * avatarRig.leftEyeRadiusX * 0.32 * freezeFactorL
        const leftShiftY = gaze.left.y * avatarRig.leftEyeRadiusY * 0.44 * freezeFactorL
        for (let i = 0; i < IRIS_INDICES.leftRing.length; i += 1) {
          const idx = IRIS_INDICES.leftRing[i]
          if (idx < eyeCanonical.length && eyeCanonical[idx]) {
            eyeCanonical[idx].x += leftShiftX
            eyeCanonical[idx].y += leftShiftY
          }
        }
        if (
          IRIS_INDICES.leftCenter < eyeCanonical.length &&
          eyeCanonical[IRIS_INDICES.leftCenter]
        ) {
          eyeCanonical[IRIS_INDICES.leftCenter].x += leftShiftX
          eyeCanonical[IRIS_INDICES.leftCenter].y += leftShiftY
        }
      }

      if (avatarRig.rightEyeCenter) {
        const rightShiftX = gaze.right.x * avatarRig.rightEyeRadiusX * 0.32 * freezeFactorR
        const rightShiftY = gaze.right.y * avatarRig.rightEyeRadiusY * 0.44 * freezeFactorR
        for (let i = 0; i < IRIS_INDICES.rightRing.length; i += 1) {
          const idx = IRIS_INDICES.rightRing[i]
          if (idx < eyeCanonical.length && eyeCanonical[idx]) {
            eyeCanonical[idx].x += rightShiftX
            eyeCanonical[idx].y += rightShiftY
          }
        }
        if (
          IRIS_INDICES.rightCenter < eyeCanonical.length &&
          eyeCanonical[IRIS_INDICES.rightCenter]
        ) {
          eyeCanonical[IRIS_INDICES.rightCenter].x += rightShiftX
          eyeCanonical[IRIS_INDICES.rightCenter].y += rightShiftY
        }
      }

      const rtEyeWarp = runtime?.eyeWarpDstPoints || runtime?.eyeOverlayPoints || null
      if (rtEyeWarp) {
        eyeWarpDstPoints = rtEyeWarp
        eyeOverlayPoints = rtEyeWarp
      } else {
        eyeWarpDstPoints = canonicalToCanvas(eyeCanonical, rigidMetrics, eyeWarpDstPoints)
        eyeOverlayPoints = eyeWarpDstPoints
      }
    }

    const rtWarp = runtime?.warpDstPoints || runtime?.dstPoints || null
    const rtWarpDepth = runtime?.warpDepthBuffer || runtime?.dstDepthBuffer || null
    if (rtWarp) {
      warpDstPoints = rtWarp
    } else {
      warpDstPoints = canonicalToCanvas(warpCanonical, rigidMetrics, warpDstPoints)
    }
    let baselineBrowMinY = null
    if (baselineCanonical && liveRigMetrics) {
      baselineCanvasPoints = canonicalToCanvas(
        baselineCanonical,
        rigidMetrics,
        baselineCanvasPoints
      )
      baselineBrowMinY = computeBrowMinY(baselineCanvasPoints, FACE_MESH_CONTOUR_PATHS)
    }
    const faceWarpDstPoints = expandFaceOvalPointsByIndices(
      warpDstPoints,
      FACE_MESH_CONTOUR_PATHS,
      baselineBrowMinY != null ? { browMinYOverride: baselineBrowMinY } : {}
    )
    let faceWarpSrcPoints = avatarRig?._faceWarpSrcPoints || null
    if (!faceWarpSrcPoints && avatarRig?.pointsPx?.length) {
      faceWarpSrcPoints = expandFaceOvalPointsByIndices(avatarRig.pointsPx, FACE_MESH_CONTOUR_PATHS)
      if (faceWarpSrcPoints !== avatarRig.pointsPx) {
        avatarRig._faceWarpSrcPoints = faceWarpSrcPoints
      }
    }
    if (rtWarpDepth && rtWarpDepth.length >= resolvedCount) {
      warpDepthBuffer = rtWarpDepth
    } else {
      if (!warpDepthBuffer || warpDepthBuffer.length < resolvedCount) {
        warpDepthBuffer = new Float32Array(resolvedCount)
      }
      for (let i = 0; i < resolvedCount; i += 1) {
        const dynamicDepth = warpCanonical[i].z || 0
        const staticDepth = avatarRig.depthBias?.[i] || 0
        // Invert MediaPipe depth (negative=close) so that positive=close.
        // This physically points the face outwards to the camera for WebGL and depth-sorting.
        warpDepthBuffer[i] = clamp(-dynamicDepth * 0.6 + staticDepth, -0.35, 0.35)
      }
    }

    if (showAvatarEyes) {
      const baseEyePoints = warpDstPoints || eyeOverlayPoints
      const irisEyePoints = eyeOverlayPoints || warpDstPoints
      const eyeClipPoints = eyeSocketDstPoints || warpDstPoints || baseEyePoints

      if (eyeClipPoints) {
        try {
          ctx.save()
          blinkCache.left = eyeRenderBlinkLeft
          blinkCache.right = eyeRenderBlinkRight
          try {
            fillEyeSockets(ctx, eyeClipPoints, {
              ...settings,
              blink: blinkCache
            })
          } catch {}
          ctx.save()
          try {
            clipToEyeSockets(ctx, eyeClipPoints)
          } catch {}
          const override = getIrisOverride(settings)
          const palette = override || getPupilPalette(avatarLayers)
          try {
            drawPupilCircles(ctx, irisEyePoints || eyeClipPoints, palette, {
              ...settings,
              blink: blinkCache
            })
          } catch {}
          ctx.restore()
          ctx.restore()
          if (trace) trace('eyesDrawn')
        } catch {}
      }
    }

    let geometryRenderer = null
    try {
      geometryRenderer = geometryManager?.ensure
        ? geometryManager.ensure()
        : geometryManager?.getRenderer?.()
    } catch {}

    const hairOpaque = Number(avatarLayers?.hairOpaque || 0)
    const hairAnchorOffsetRatio = Number(avatarLayers?.hairAnchorOffsetRatio) || 0
    const minHairOpaque = 16
    const hairMaskReady = hairOpaque > minHairOpaque
    const hairMeshTriCount = avatarLayers?.hairMesh?.triangles?.length || 0
    const hairTriCount = (avatarLayers?.hairTriangles?.length || 0) + hairMeshTriCount
    const hairGeometryReady = showAvatarHair && hairMaskReady && hairTriCount > 0
    const faceTriCount =
      avatarLayers?.faceTriangles?.length || avatarRig?.triangles?.length || 0 || 0
    const hairTriRatio = hairTriCount / Math.max(1, faceTriCount)
    const hairGeometryCoverageOk = hairTriCount >= 18 && hairTriRatio >= 0.06
    const useGeometryHair = !prefer2dHair && hairGeometryReady && hairGeometryCoverageOk

    const leftEyeZ = liveCanonical?.[LANDMARKS.leftEye]?.z || 0
    const rightEyeZ = liveCanonical?.[LANDMARKS.rightEye]?.z || 0
    const yawDelta = leftEyeZ - rightEyeZ
    const yawAmount = clamp(Math.abs(yawDelta) * 8.5, 0, 1)
    const hairYaw = yawAmount * (Math.sign(yawDelta) || 1)

    const hairMesh = avatarLayers?.hairMesh || null
    const hairMeshTriangles =
      hairMesh?.triangles && hairMesh.triangles.length ? hairMesh.triangles : null
    let hairDstPoints = null
    let hairDstDepth = null
    if (hairMesh?.points?.length && avatarRig?.faceMetrics && liveRigMetrics) {
      const count = hairMesh.points.length
      const anchorRatio = Number.isFinite(hairMesh?.anchorOffsetRatio)
        ? hairMesh.anchorOffsetRatio
        : hairAnchorOffsetRatio
      const anchorOffset = anchorRatio ? anchorRatio * liveRigMetrics.faceH : 0
      const neighborK = Number(hairMesh.neighborK) || 0
      const neighborIdx = hairMesh.neighborIdx
      const neighborW = hairMesh.neighborW
      const neighborFalloff =
        hairMesh.neighborFalloff && hairMesh.neighborFalloff.length === count
          ? hairMesh.neighborFalloff
          : null
      const neighborBlend = Number.isFinite(hairMesh.neighborBlend) ? hairMesh.neighborBlend : 0.7
      const canWarpHair =
        neighborK > 0 &&
        neighborIdx?.length >= count * neighborK &&
        neighborW?.length >= count * neighborK &&
        faceWarpDstPoints?.length &&
        faceWarpSrcPoints?.length

      if (!hairMeshDstPoints || hairMeshPointCount !== count) {
        hairMeshDstPoints = new Array(count)
        for (let i = 0; i < count; i += 1) hairMeshDstPoints[i] = { x: 0, y: 0 }
        hairMeshPointCount = count
      }

      const baseFaceW = Math.max(1, avatarRig.faceMetrics.faceW)
      const baseFaceH = Math.max(1, avatarRig.faceMetrics.faceH)
      const scaleX = rigidMetrics.faceW / baseFaceW
      const scaleY = rigidMetrics.faceH / baseFaceH
      const rot = liveRigMetrics.rotation || 0
      const cos = Math.cos(rot)
      const sin = Math.sin(rot)
      const yawMag = Math.min(1, Math.abs(hairYaw))
      const yawScaleX = 1 - yawMag * 0.22
      const yawShift = (Math.sign(hairYaw) || 1) * yawMag * (liveRigMetrics.faceW * 0.12)
      const cx = avatarRig.faceMetrics.centerX
      const cy = avatarRig.faceMetrics.centerY

      let faceRigidPoints = null
      if (canWarpHair) {
        const faceCount = faceWarpSrcPoints.length
        if (!faceRigidDstPoints || faceRigidPointCount !== faceCount) {
          faceRigidDstPoints = new Array(faceCount)
          for (let i = 0; i < faceCount; i += 1) faceRigidDstPoints[i] = { x: 0, y: 0 }
          faceRigidPointCount = faceCount
        }
        faceRigidPoints = faceRigidDstPoints
        for (let i = 0; i < faceCount; i += 1) {
          const src = faceWarpSrcPoints[i]
          const ref = src || { x: cx, y: cy }
          const dx = (ref.x - cx) * scaleX * yawScaleX
          const dy = (ref.y - cy) * scaleY
          const x = dx * cos - dy * sin + liveRigMetrics.centerX + yawShift
          const y = dx * sin + dy * cos + liveRigMetrics.centerY
          const dst = faceRigidPoints[i]
          dst.x = x
          dst.y = y
        }
      }

      for (let i = 0; i < count; i += 1) {
        const src = hairMesh.points[i]
        const dx = (src.x - cx) * scaleX * yawScaleX
        const dy = (src.y - cy) * scaleY
        let x = dx * cos - dy * sin + liveRigMetrics.centerX + yawShift
        let y = dx * sin + dy * cos + liveRigMetrics.centerY

        if (canWarpHair && faceRigidPoints) {
          const base = i * neighborK
          let deltaX = 0
          let deltaY = 0
          let wsum = 0
          for (let t = 0; t < neighborK; t += 1) {
            const w = neighborW[base + t] || 0
            if (!w) continue
            const idx = neighborIdx[base + t] || 0
            const faceDst = faceWarpDstPoints[idx]
            const faceRigid = faceRigidPoints[idx]
            if (!faceDst || !faceRigid) continue
            deltaX += (faceDst.x - faceRigid.x) * w
            deltaY += (faceDst.y - faceRigid.y) * w
            wsum += w
          }
          if (wsum > 0) {
            const falloff = neighborFalloff ? neighborFalloff[i] || 0 : 1
            const influence = falloff * neighborBlend
            if (influence > 0) {
              x += deltaX * influence
              y += deltaY * influence
            }
          }
        }
        if (anchorOffset) {
          const falloff = neighborFalloff ? neighborFalloff[i] || 0 : 0.25
          if (falloff > 0) y += anchorOffset * falloff
        }
        const dst = hairMeshDstPoints[i]
        dst.x = x
        dst.y = y
      }
      if (hairMesh.depth && hairMesh.depth.length === count) {
        hairDstDepth = hairMesh.depth
      }
      hairDstPoints = hairMeshDstPoints
    }

    const canWarpHairMesh2d =
      showAvatarHair &&
      !useGeometryHair &&
      hairMesh?.points?.length &&
      hairMeshTriangles &&
      hairDstPoints &&
      hairDstPoints.length === hairMesh.points.length

    const frontCanvas = hairOpaque > minHairOpaque ? avatarLayers?.hairCanvas : null
    const hairAnchorOffset = hairAnchorOffsetRatio * (liveRigMetrics?.faceH || 0)

    try {
      renderedByGeometry =
        geometryRenderer?.renderToContext(ctx, {
          dstPoints: faceWarpDstPoints || warpDstPoints,
          dstDepth: warpDepthBuffer,
          hairDstPoints,
          hairDstDepth,
          drawFace: showAvatarFace,
          drawHair: !!useGeometryHair,
          depthStrength: settings.geometryDepth * 0.24
        }) === true
    } catch {
      renderedByGeometry = false
    }

    if (!renderedByGeometry) {
      if (showAvatarFace) {
        const warpSource =
          (showAvatarEyes && avatarLayers?.faceNoIrisCanvas) ||
          avatarLayers?.faceCanvas ||
          avatarImage.img
        const stride = meshQualityToStride(settings.meshQuality)
        const rawTriangles =
          avatarLayers?.expressionTriangles || avatarLayers?.faceTriangles || avatarRig.triangles
        if (
          faceTriangleWarpCache.source !== rawTriangles ||
          faceTriangleWarpCache.stride !== stride ||
          !faceTriangleWarpCache.triangles?.length
        ) {
          const noDecimate = settings.meshQuality === 'high' || showAvatarEyes === true
          faceTriangleWarpCache = {
            source: rawTriangles,
            stride,
            triangles: noDecimate ? rawTriangles : decimateTriangles(rawTriangles, stride)
          }
        }
        const faceTriangles = faceTriangleWarpCache.triangles || rawTriangles
        warpTriangles(
          ctx,
          warpSource,
          faceWarpSrcPoints || avatarRig.pointsPx,
          faceWarpDstPoints || warpDstPoints,
          faceTriangles,
          { depths: warpDepthBuffer }
        )
      }
    }

    // Always draw eyelid occluders as the final eye layer.
    if (showAvatarEyes) {
      try {
        const eyeClipPoints = eyeSocketDstPoints || warpDstPoints || eyeWarpDstPoints
        const irisPointsFinal = eyeWarpDstPoints || warpDstPoints
        const lidPts = eyeClipPoints || irisPointsFinal
        if (lidPts) {
          try {
            drawEyelidOccluder(ctx, lidPts, 'left', eyelidBlinkLeft, {
              poly: EYE_LAYER_POLYGONS.left,
              color:
                settings.lidColor ||
                sampleLidColor(adapter, 'left', avatarImage, avatarRig, avatarKey),
              start: lidStart,
              full: lidFull
            })
          } catch {}
          try {
            drawEyelidOccluder(ctx, lidPts, 'right', eyelidBlinkRight, {
              poly: EYE_LAYER_POLYGONS.right,
              color:
                settings.lidColor ||
                sampleLidColor(adapter, 'right', avatarImage, avatarRig, avatarKey),
              start: lidStart,
              full: lidFull
            })
          } catch {}
        }
      } catch {}
    }

    if (
      settings._allowDebugOverlays === true &&
      settings.faceMeshRender &&
      settings.faceMeshRender !== 'none'
    ) {
      const overlayPts = faceWarpDstPoints || warpDstPoints || eyeWarpDstPoints
      if (overlayPts) {
        drawFaceMeshOverlay(ctx, overlayPts, settings.faceMeshRender)
      }
    }

    const faceHasEyeMask = !!(showAvatarFace && avatarLayers?.faceNoIrisCanvas)
    if (renderedByGeometry && showAvatarFace && showAvatarEyes && !faceHasEyeMask) {
      const eyeClipPoints = eyeSocketDstPoints || warpDstPoints || eyeWarpDstPoints
      const irisPointsFinal = eyeWarpDstPoints || warpDstPoints
      if (eyeClipPoints && irisPointsFinal) {
        ctx.save()
        clipToEyeSockets(ctx, eyeClipPoints)
        const irisStyle = settings.irisStyle || 'image'
        const canUseIrisImage = irisStyle === 'image' && !!avatarLayers?.irisCanvas
        if (!canUseIrisImage) {
          blinkCache.left = eyeRenderBlinkLeft
          blinkCache.right = eyeRenderBlinkRight
          const override = getIrisOverride(settings)
          const palette = override || getPupilPalette(avatarLayers)
          drawPupilCircles(ctx, irisPointsFinal, palette, {
            ...settings,
            blink: blinkCache
          })
        } else {
          const leftStats = computeIrisStats(
            irisPointsFinal,
            IRIS_INDICES.leftCenter,
            IRIS_INDICES.leftRing
          )
          const rightStats = computeIrisStats(
            irisPointsFinal,
            IRIS_INDICES.rightCenter,
            IRIS_INDICES.rightRing
          )
          const addCircle = (s) => {
            if (!s) return false
            const r = Math.max(1.1, s.radius * 0.98)
            ctx.moveTo(s.cx + r, s.cy)
            ctx.arc(s.cx, s.cy, r, 0, Math.PI * 2)
            return true
          }
          ctx.save()
          ctx.beginPath()
          let any = false
          any = addCircle(leftStats) || any
          any = addCircle(rightStats) || any
          if (any) ctx.clip()
          const irisTriFinal = avatarLayers?.irisTriangles?.length
            ? avatarLayers.irisTriangles
            : null
          if (irisTriFinal?.length) {
            warpTriangles(
              ctx,
              avatarLayers.irisCanvas,
              avatarRig.pointsPx,
              irisPointsFinal,
              irisTriFinal
            )
          }
          ctx.restore()
        }
        ctx.restore()
      }
    }

    if (showAvatarHair && (!renderedByGeometry || !useGeometryHair)) {
      if (frontCanvas) {
        if (canWarpHairMesh2d) {
          warpTriangles(ctx, frontCanvas, hairMesh.points, hairDstPoints, hairMeshTriangles)
        } else {
          // Draw full hair rigidly aligned to the face rig.
          drawRigAlignedLayerWithYaw(
            ctx,
            frontCanvas,
            avatarRig,
            liveRigMetrics,
            hairYaw,
            1,
            hairAnchorOffset
          )
        }
      }
    }

    return { browSmoothL, browSmoothR, cheekSmoothL, cheekSmoothR, renderedByGeometry }
  }

  return { processMesh, reset }
}

export default { createMeshRenderer }
